import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home, Building2, CalendarDays, LayoutDashboard, Users, Store, ContactRound,
  Landmark, FolderOpen, BarChart3, AppWindow, Wallet, Receipt, Wrench, Bell,
  Search, ChevronDown, LogOut, Settings, UserCircle2, Database, Globe, Printer,
} from 'lucide-react';
import { printNode } from '../../utils/print';
import { fileUrl } from '../../services/api';

const NAV_BUTTONS = [
  { label: 'Home', path: '/dashboard', icon: Home },
  { label: 'My Company', path: '/settings/company', icon: Building2 },
  { label: 'Calendar', path: '/settings/fiscal', icon: CalendarDays },
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Customers', path: '/customers', icon: Users },
  { label: 'Vendors', path: '/vendors', icon: Store },
  { label: 'Employees', path: '/employees', icon: ContactRound },
  { label: 'Banking', path: '/banking', icon: Landmark },
  { label: 'Documents', path: '/documents', icon: FolderOpen },
  { label: 'Reports', path: '/reports', icon: BarChart3 },
  { label: 'Applications', path: '/applications', icon: AppWindow },
  { label: 'Payroll', path: '/payroll', icon: Wallet },
  { label: 'Expenses', path: '/expenses', icon: Receipt },
  { label: 'Services', path: '/services', icon: Wrench },
  { label: 'Notifications', path: '/notifications', icon: Bell },
];

/**
 * Dark navy application toolbar: icon+text module buttons, a global
 * search field, notification bell and a user account menu on the right.
 */
export default function ApplicationToolbar({ user, subscription, isPendingSubscription, isTrialing, trialDaysLeft, onLogout, activePath, searchProps }) {
  const navigate = useNavigate();
  const [userOpen, setUserOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const rightRef = useRef(null);
  const subRef = useRef(null);

  const { query, setQuery, results, showResults, onSelect } = searchProps || {};

  // Calculate subscription display info
  const getSubInfo = () => {
    if (!subscription) return null;
    const status = subscription.status;
    const planName = subscription.plan_name || 'Plan';
    let daysLeft = 0;
    let label = '';
    let color = '';
    let bgColor = '';
    let borderColor = '';

    if (status === 'active') {
      const endDate = subscription.current_period_end;
      if (endDate) {
        const diff = new Date(endDate) - new Date();
        daysLeft = Math.max(0, Math.ceil(diff / 86400000));
      }
      label = `${daysLeft}d left`;
      color = daysLeft <= 7 ? '#92400e' : '#166534';
      bgColor = daysLeft <= 7 ? '#fef3c7' : '#dcfce7';
      borderColor = daysLeft <= 7 ? '#fde68a' : '#bbf7d0';
    } else if (status === 'trialing') {
      daysLeft = trialDaysLeft ? trialDaysLeft() : 0;
      label = `Trial · ${daysLeft}d left`;
      color = '#1e40af';
      bgColor = '#eff6ff';
      borderColor = '#bfdbfe';
    } else if (status === 'pending') {
      label = 'Pending';
      color = '#92400e';
      bgColor = '#fffbeb';
      borderColor = '#fde68a';
    } else if (status === 'expired') {
      label = 'Expired';
      color = '#991b1b';
      bgColor = '#fef2f2';
      borderColor = '#fecaca';
    } else {
      return null;
    }

    return { planName, label, color, bgColor, borderColor, status };
  };

  const subInfo = getSubInfo();

  useEffect(() => {
    const onDocClick = (e) => {
      if (rightRef.current && !rightRef.current.contains(e.target)) {
        setUserOpen(false);
        setNotifOpen(false);
      }
      if (subRef.current && !subRef.current.contains(e.target)) {
        setSubOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const isActive = (path) => {
    if (path === '/dashboard') return activePath === '/dashboard' || activePath === '';
    return activePath === path || (path !== '/' && activePath.startsWith(path));
  };

  const initials = () => {
    const name = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
    const base = name || user?.username || 'U';
    return base.split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
  };

  return (
    <div className="desk-toolbar">
      {NAV_BUTTONS.map((btn, i) => (
        <React.Fragment key={btn.label}>
          {i === 4 && <div className="desk-tool-sep" />}
          <button
            type="button"
            className={`desk-tool-btn ${isActive(btn.path) ? 'active' : ''}`}
            title={btn.label}
            onClick={() => navigate(btn.path)}
          >
            <btn.icon />
            <span>{btn.label}</span>
          </button>
        </React.Fragment>
      ))}

      <div className="desk-toolbar-right" ref={rightRef}>
        {subInfo && (
          <div ref={subRef} style={{ position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '3px 10px',
                borderRadius: 4,
                border: `1px solid ${subInfo.borderColor}`,
                background: subInfo.bgColor,
                color: subInfo.color,
                fontSize: 11,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                lineHeight: 1,
              }}
              title={`${subInfo.planName} — ${subInfo.label}`}
              onClick={() => setSubOpen(o => !o)}
            >
              <span style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: subInfo.status === 'active' ? '#16a34a' : subInfo.status === 'trialing' ? '#2563eb' : subInfo.status === 'pending' ? '#d97706' : '#dc2626',
                flexShrink: 0,
              }} />
              <span>{subInfo.planName}</span>
              <span style={{ opacity: 0.7 }}>·</span>
              <span>{subInfo.label}</span>
              <ChevronDown size={10} style={{ opacity: 0.5 }} />
            </div>
            {subOpen && subscription && (
              <div className="desk-popover" style={{ minWidth: 260, top: 'calc(100% + 6px)', insetInlineEnd: 0, zIndex: 200 }}>
                <div className="desk-popover-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: subInfo.status === 'active' ? '#16a34a' : subInfo.status === 'trialing' ? '#2563eb' : subInfo.status === 'pending' ? '#d97706' : '#dc2626',
                  }} />
                  <span>{subInfo.planName}</span>
                  <span style={{
                    marginLeft: 'auto', fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3,
                    background: subInfo.bgColor, color: subInfo.color, border: `1px solid ${subInfo.borderColor}`,
                  }}>{subInfo.label}</span>
                </div>
                <div style={{ padding: '8px 14px', fontSize: 12 }}>
                  {/* Billing Cycle */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: '#64748b' }}>Billing Cycle</span>
                    <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{subscription.billing_cycle || 'Monthly'}</span>
                  </div>
                  {/* Current Period */}
                  {subscription.current_period_start && subscription.current_period_end && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#64748b' }}>Current Period</span>
                      <span style={{ fontWeight: 500, fontSize: 11, textAlign: 'right' }}>
                        {new Date(subscription.current_period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {' — '}
                        {new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                  {/* Trial End */}
                  {subscription.status === 'trialing' && subscription.trial_ends_at && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#64748b' }}>Trial Ends</span>
                      <span style={{ fontWeight: 500 }}>
                        {new Date(subscription.trial_ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                  <div style={{ height: 1, background: '#e2e8f0', margin: '8px 0' }} />
                  {/* Plan Limits */}
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Plan Limits</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: '#64748b' }}>👥 Max Users</span>
                    <span style={{ fontWeight: 600 }}>{subscription.max_users ?? '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: '#64748b' }}>📄 Invoices / Month</span>
                    <span style={{ fontWeight: 600 }}>{subscription.max_invoices_month ?? '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: '#64748b' }}>💾 Storage</span>
                    <span style={{ fontWeight: 600 }}>{subscription.max_storage_mb != null ? `${subscription.max_storage_mb} MB` : '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: '#64748b' }}>🔄 Auto-Renew</span>
                    <span style={{ fontWeight: 600 }}>{subscription.auto_renew ? 'Yes' : 'No'}</span>
                  </div>
                </div>
                {user?.company_id && (
                  <div style={{ padding: '6px 14px 10px', borderTop: '1px solid #e2e8f0' }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      style={{ width: '100%', justifyContent: 'center' }}                       onClick={() => { setSubOpen(false); navigate('/subscription'); }}
                    >
                      Manage Subscription
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="desk-toolbar-search">
          <Search size={14} />
          <input
            placeholder="Search…"
            value={query || ''}
            onChange={(e) => setQuery && setQuery(e.target.value)}
            onFocus={() => showResults && results?.length > 0}
          />
          {showResults && results?.length > 0 && (
            <div className="desk-popover" style={{ top: 'calc(100% + 8px)', insetInlineEnd: 0, minWidth: 260 }}>
              {results.slice(0, 8).map((r, i) => (
                <button type="button" className="desk-popover-item" key={i} onClick={() => onSelect && onSelect(r)}>
                  <span className="desk-menu-icon" style={{ textTransform: 'uppercase', fontSize: 10, fontWeight: 700, color: 'var(--desk-text-3)' }}>
                    {r.module}
                  </span>
                  <span>{r.name || r.number || r.description}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          className="desk-icon-btn"
          title="Print this page"
          aria-label="Print"
          onClick={() => {
            const el = document.querySelector('.page-content');
            if (el) printNode(el, document.title || 'Print');
          }}
        >
          <Printer size={17} />
        </button>

        <button type="button" className="desk-icon-btn" title="Notifications" aria-label="Notifications" onClick={() => setNotifOpen(o => !o)}>
          <Bell size={17} />
          <span className="desk-notif-dot" />
        </button>
        {notifOpen && (
          <div className="desk-popover" style={{ minWidth: 240 }}>
            <div className="desk-popover-header">Notifications</div>
            <button type="button" className="desk-popover-item" onClick={() => { setNotifOpen(false); navigate('/bills'); }}>
              <Receipt size={14} /> 1 bill awaiting approval
            </button>
            <button type="button" className="desk-popover-item" onClick={() => { setNotifOpen(false); navigate('/invoices'); }}>
              <Users size={14} /> 3 invoices past due
            </button>
            <button type="button" className="desk-popover-item" onClick={() => { setNotifOpen(false); navigate('/settings/backup'); }}>
              <Database size={14} /> Backup completed successfully
            </button>
          </div>
        )}

        <button type="button" className="desk-user-menu" onClick={() => setUserOpen(o => !o)}>
          {user?.avatar_path
            ? <img className="desk-avatar desk-avatar-img" src={fileUrl(user.avatar_path)} alt="" />
            : <span className="desk-avatar">{initials()}</span>}
          <span>{user?.first_name || user?.username}</span>
          <ChevronDown size={13} />
        </button>
        {userOpen && (
          <div className="desk-popover" style={{ minWidth: 230 }}>
            <div className="desk-popover-header">
              {user?.first_name} {user?.last_name} · {user?.role_name || 'User'}
            </div>
            {/* Subscription plan details for company users */}
            {subscription && user?.company_id && (
              <div style={{ padding: '8px 14px', borderBottom: '1px solid #e2e8f0', fontSize: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Subscription Plan</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ color: '#64748b' }}>Plan</span>
                  <span style={{ fontWeight: 600 }}>{subscription.plan_name || '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ color: '#64748b' }}>Status</span>
                  <span style={{
                    display: 'inline-block', padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700,
                    background: subscription.status === 'active' ? '#dcfce7' : subscription.status === 'trialing' ? '#dbeafe' : '#fef2f2',
                    color: subscription.status === 'active' ? '#166534' : subscription.status === 'trialing' ? '#1e40af' : '#991b1b',
                  }}>{subscription.status || '—'}</span>
                </div>
                {subscription.current_period_end && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ color: '#64748b' }}>Renewal</span>
                    <span style={{ fontWeight: 500, fontSize: 11 }}>{new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                )}
                {subscription.max_users && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Users</span>
                    <span style={{ fontWeight: 500 }}>{subscription.max_users} max</span>
                  </div>
                )}
              </div>
            )}
            <button type="button" className="desk-popover-item" onClick={() => { setUserOpen(false); navigate('/settings/profile'); }}>
              <UserCircle2 size={14} /> My Profile
            </button>
            <button type="button" className="desk-popover-item" onClick={() => { setUserOpen(false); navigate('/settings/company'); }}>
              <Settings size={14} /> Company Settings
            </button>
            <button type="button" className="desk-popover-item" onClick={() => { setUserOpen(false); navigate('/settings/backup'); }}>
              <Database size={14} /> Backup &amp; Recovery
            </button>
            <button type="button" className="desk-popover-item" onClick={() => { setUserOpen(false); toggleDir(); }}>
              <Globe size={14} /> RTL / English
            </button>
            <div className="desk-menu-sep" />
            <button type="button" className="desk-popover-item" onClick={() => { setUserOpen(false); onLogout && onLogout(); }}>
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function toggleDir() {
  const html = document.documentElement;
  const next = html.dir === 'rtl' ? 'ltr' : 'rtl';
  html.dir = next;
  html.lang = next === 'rtl' ? 'fa' : 'en';
  try { localStorage.setItem('ledgerpro_dir', next); } catch { /* noop */ }
}
